import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { TalentApplications } from "@/components/admin/TalentApplications";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, UserPlus, Ban, Power } from "lucide-react";

export default function Talents() {
  const [talents, setTalents] = useState([]);
  const [meta, setMeta] = useState({ banks: [], platforms: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", platform: "TikTok Shop", tier: "Silver",
    bank_id: 3, account_number: "", account_holder: "", password: "Talent@123",
  });

  const load = () => {
    api.get("/talents").then((res) => setTalents(res.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/meta").then((res) => setMeta(res.data)).catch(() => {});
  }, []);

  const submit = async () => {
    const bank = meta.banks.find((b) => b.bank_id === Number(form.bank_id));
    try {
      const { data } = await api.post("/talents", {
        ...form,
        bank_id: Number(form.bank_id),
        bank_name: bank?.name || "Bank BCA",
        payment_method: bank?.method || 1,
        account_holder: form.account_holder || form.name,
      });
      toast.success(`Talent ${data.name} ditambahkan (password portal: ${data.portal_password})`);
      setOpen(false);
      setForm({ name: "", email: "", phone: "", platform: "TikTok Shop", tier: "Silver", bank_id: 3, account_number: "", account_holder: "", password: "Talent@123" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleStatus = async (t) => {
    const status = t.status === "active" ? "inactive" : "active";
    try {
      await api.put(`/talents/${t.id}`, { status });
      toast.success(status === "inactive" ? `${t.name} dinonaktifkan: login & penarikan diblokir` : `${t.name} diaktifkan kembali`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/talents/${id}`);
      toast.success("Talent dihapus");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-talents-container">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Manajemen Talent</h1>
          <p className="text-muted-foreground mt-1">Kelola afiliasi & detail rekening disbursement</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-talent-button" className="bg-primary hover:bg-primary/90 text-white">
              <Plus className="h-4 w-4 mr-2" /> Tambah Talent
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2"><UserPlus className="h-5 w-5 text-primary" /> Talent Baru</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2">
                <Label>Nama Lengkap</Label>
                <Input data-testid="talent-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="talent-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>Telepon</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>Platform</Label>
                <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.platforms.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tier</Label>
                <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Silver", "Gold", "Platinum"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bank / E-Wallet</Label>
                <Select value={String(form.bank_id)} onValueChange={(v) => setForm({ ...form, bank_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="talent-bank-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.banks.map((b) => <SelectItem key={b.bank_id} value={String(b.bank_id)}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nomor Rekening / HP</Label>
                <Input data-testid="talent-account-input" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} className="mt-1.5" />
              </div>
              <div className="col-span-2">
                <Label>Password Portal Talent</Label>
                <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="talent-save-button" onClick={submit} className="bg-primary hover:bg-primary/90 text-white w-full">Simpan Talent</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <TalentApplications applications={talents.filter((t) => t.status === "pending" || t.status === "rejected")} onChange={load} />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="talents-list">
        {talents.filter((t) => t.status !== "pending" && t.status !== "rejected").map((t) => (
          <div key={t.id} className="bg-card border border-border rounded-xl p-5 hover:border-gray-700 transition-colors" data-testid={`talent-card-${t.id}`}>
            <div className="flex items-start gap-3">
              <img src={t.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(t.name)}&background=6366F1&color=fff`} alt={t.name} className="h-12 w-12 rounded-full object-cover border border-border" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="font-semibold truncate">{t.name}</p>
                  <StatusBadge status={t.status} />
                </div>
                <p className="text-xs text-muted-foreground truncate">{t.email}</p>
                <div className="flex gap-2 mt-1">
                  <span className="text-[10px] px-2 py-0.5 rounded bg-primary/15 text-primary border border-primary/20">{t.tier}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-secondary text-muted-foreground">{t.platform}</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4 text-center">
              <div className="bg-background rounded-lg p-2">
                <p className="text-[10px] text-muted-foreground">Total Komisi</p>
                <p className="text-xs font-mono font-semibold text-amber-400">{rupiah(t.balance?.total_earned)}</p>
              </div>
              <div className="bg-background rounded-lg p-2">
                <p className="text-[10px] text-muted-foreground">Tersedia</p>
                <p className="text-xs font-mono font-semibold text-emerald-400">{rupiah(t.balance?.available)}</p>
              </div>
              <div className="bg-background rounded-lg p-2">
                <p className="text-[10px] text-muted-foreground">Ditarik</p>
                <p className="text-xs font-mono font-semibold text-cyan-400">{rupiah(t.balance?.withdrawn)}</p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4 text-xs text-muted-foreground">
              <span className="font-mono">{t.bank_name} • {t.account_number}</span>
              <div className="flex items-center gap-3">
              <button
                data-testid={`toggle-talent-status-${t.id}`}
                onClick={() => toggleStatus(t)}
                className={`flex items-center gap-1 transition-colors ${t.status === "active" ? "hover:text-amber-400" : "text-emerald-400 hover:text-emerald-300"}`}
              >
                {t.status === "active" ? <><Ban className="h-3.5 w-3.5" /> Nonaktifkan</> : <><Power className="h-3.5 w-3.5" /> Aktifkan</>}
              </button>
              <button data-testid={`delete-talent-${t.id}`} onClick={() => remove(t.id)} className="text-muted-foreground hover:text-destructive transition-colors">
                <Trash2 className="h-4 w-4" />
              </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
